import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import apiClient from "../../services/apiClient";
import SEO from "../../components/SEO";
import {
  getApproachabilityLabel,
  getPriorityLabel,
} from "../constants/prospectLabels";

export default function AdminProspectsPage() {
  const [q, setQ] = useState("");
  const [minScore, setMinScore] = useState("65");
  const [priority, setPriority] = useState("");
  const [approachability, setApproachability] = useState("");
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (minScore) params.set("min_score", minScore);
      if (priority) params.set("priority_level", priority);
      if (approachability) params.set("approachability_label", approachability);
      const result = await apiClient.adminProspectsShortlist(params.toString());
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [q, minScore, priority, approachability]);

  useEffect(() => {
    load();
  }, [load]);

  const getPriorityColor = (value) => {
    if (value === "high") return "error";
    if (value === "medium") return "warning";
    if (value === "low") return "info";
    return "default";
  };

  const getApproachColor = (value) => {
    if (value === "approachable_now") return "success";
    if (value === "enrich_before_contact") return "warning";
    if (value === "exclude") return "error";
    return "default";
  };

  const runQuickAction = async (id, action) => {
    setBusyId(id);
    setMessage({ type: "", text: "" });
    try {
      if (action === "contacted") {
        await apiClient.adminProspectPatch(id, {
          qualification_status: "contacted",
        });
        setMessage({ type: "success", text: "Prospect marque comme contacte." });
      }
      if (action === "excluded") {
        await apiClient.adminProspectPatch(id, {
          qualification_status: "excluded",
          exclusion_reason: "Exclusion manuelle",
        });
        setMessage({ type: "success", text: "Prospect marque comme exclu." });
      }
      await load();
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Action impossible." });
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <SEO title="Shortlist prets a contacter | DigiBuddy" noIndex noFollow />
      <Typography variant="h4" sx={{ mb: 2 }}>
        3. Shortlist prets a contacter
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Cette vue montre les meilleurs prospects actionnables, deja filtres pour le contact.
      </Typography>
      {message.text ? (
        <Alert severity={message.type || "info"} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Recherche entreprise/email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
        />
        <TextField
          label="Score min"
          value={minScore}
          onChange={(e) => setMinScore(e.target.value)}
          size="small"
          sx={{ width: 120 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Priorite</InputLabel>
          <Select
            value={priority}
            label="Priorite"
            onChange={(e) => setPriority(e.target.value)}
          >
            <MenuItem value="">Toutes</MenuItem>
            <MenuItem value="high">Haute</MenuItem>
            <MenuItem value="medium">Moyenne</MenuItem>
            <MenuItem value="low">Basse</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Approchabilite</InputLabel>
          <Select
            value={approachability}
            label="Approchabilite"
            onChange={(e) => setApproachability(e.target.value)}
          >
            <MenuItem value="">Toutes</MenuItem>
            <MenuItem value="approachable_now">Approchable maintenant</MenuItem>
            <MenuItem value="enrich_before_contact">A enrichir avant contact</MenuItem>
            <MenuItem value="watchlist">A surveiller</MenuItem>
            <MenuItem value="exclude">A exclure</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={load} disabled={loading}>
          Filtrer
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setQ("");
            setMinScore("65");
            setPriority("");
            setApproachability("");
          }}
        >
          Reinitialiser
        </Button>
        <Button
          variant="outlined"
          component="a"
          href={apiClient.adminProspectsExportCsvUrl(
            new URLSearchParams({
              ...(q ? { q } : {}),
              ...(minScore ? { min_score: minScore } : {}),
            }).toString()
          )}
          target="_blank"
          rel="noreferrer"
        >
          Export CSV
        </Button>
        <Button component={Link} to="/admin/prospects/why-not" variant="text">
          Pourquoi exclus ?
        </Button>
      </Box>

      <Paper sx={{ overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entreprise</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Priorite</TableCell>
              <TableCell>Approchabilite</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telephone</TableCell>
              <TableCell>Angle</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.rows || []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.company_name}</TableCell>
                <TableCell>{row.global_score}/100</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={getPriorityColor(row.priority_level)}
                    label={getPriorityLabel(row.priority_level)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={getApproachColor(row.approachability_label)}
                    label={getApproachabilityLabel(row.approachability_label)}
                  />
                </TableCell>
                <TableCell>{row.primary_contact?.full_name || "-"}</TableCell>
                <TableCell>{row.primary_contact?.email || "-"}</TableCell>
                <TableCell>{row.primary_contact?.phone || "-"}</TableCell>
                <TableCell>{row.primary_angle || "-"}</TableCell>
                <TableCell>
                  <Button size="small" component={Link} to={`/admin/prospects/${row.id}`}>
                    Voir
                  </Button>
                  <Button
                    size="small"
                    color="success"
                    onClick={() => runQuickAction(row.id, "contacted")}
                    disabled={busyId === row.id}
                  >
                    Contacte
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => runQuickAction(row.id, "excluded")}
                    disabled={busyId === row.id}
                  >
                    Exclure
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
        {data.total || 0} prospect(s)
      </Typography>
    </>
  );
}
